/**
 * ScanList — the persistent list of geolocated scans beside the "Scans by location"
 * map, mirroring the certificate page's Scans tab (list left, map right, selection
 * shared both ways).
 *
 * It replaces a detail panel that only appeared once a marker had been clicked, which
 * meant the card opened with no readable inventory of what the pins were: a brand had
 * to hunt a dot to learn anything, and clustered pins hid rows entirely.
 */
import React from 'react'

import { Stack } from '@mui/material'
import { NfcIcon } from 'lucide-react'

import type { IScanMapPoint } from '@repo/types'

import { EmptyState } from 'components/display'

import { ScanListItem } from './ScanListItem'

interface ScanListProps {
  points: IScanMapPoint[]
  activeId?: string
  onItemClick?: (scanId: string) => void
  /**
   * True when the payload is one row per CERTIFICATE (the map's certificates mode,
   * `mode: 'latest'`) rather than one row per scan.
   *
   * The list does not dedupe: it renders exactly the rows the query returned, which is
   * what keeps it in step with the pins. What changes is what a row MEANS, and the one
   * place that leaks into the UI is the timestamp — per scan it is when that scan
   * happened, per certificate it is when the certificate was last seen at this place.
   */
  perCertificate?: boolean
  /**
   * Shown when the payload is empty. Supplied by the caller because only the caller
   * knows the data type, and because an empty payload has two indistinguishable causes
   * (nothing in range, or nothing in range that shared a location) that the copy must
   * not pick between.
   */
  emptyTitle?: string
  emptyText?: string
}

export const ScanList = ({
  points,
  activeId,
  onItemClick,
  perCertificate = false,
  emptyTitle = 'No scans in this range',
  emptyText = 'Adjust the date range or filters above to see scans here.',
}: ScanListProps) => {
  if (points.length === 0) {
    return (
      <Stack sx={{ height: '100%' }} alignItems="center" justifyContent="center">
        <EmptyState
          title={emptyTitle}
          text={emptyText}
          icon={<NfcIcon strokeWidth={1} size={32} />}
        />
      </Stack>
    )
  }

  return (
    // No top padding: the first row (including its selected brand tint) sits flush to
    // the top edge — a gap there reads as a stray strip above a selected first row.
    <Stack sx={{ pb: 2 }}>
      {points.map(point => (
        <ScanListItem
          key={point.scan_id}
          point={point}
          isActive={activeId != null && String(point.scan_id) === String(activeId)}
          onClick={onItemClick}
          perCertificate={perCertificate}
        />
      ))}
    </Stack>
  )
}
